import type { LocalizedText } from "../schemas/common";
import type {
  IneligibilityReasonCode,
  LineReasonCode,
  ResolutionType,
  ReturnReasonCode,
} from "../schemas/policy";
import type { ActorType, Grade, LineState } from "../schemas/return";
import type { MerchantStateGroup } from "../schemas/state-group";
import { ar } from "./ar";
import { en } from "./en";
import { MESSAGE_KEYS } from "./keys";
import type { MessageKey } from "./keys";

/**
 * The bilingual catalogue. Every user-facing string in the system resolves through here;
 * none is inline in a component or a route handler.
 *
 * Both translations are always present because `ar` and `en` are each typed
 * `Record<MessageKey, string>` — a missing translation fails the build.
 */
export const catalogue: Record<MessageKey, LocalizedText> = Object.fromEntries(
  MESSAGE_KEYS.map((key) => [key, { ar: ar[key], en: en[key] }]),
) as Record<MessageKey, LocalizedText>;

/** Resolve a key to its bilingual pair. Always returns both languages. */
export function message(key: MessageKey): LocalizedText {
  return catalogue[key];
}

/**
 * Interpolate `{name}` placeholders into both languages at once.
 * Values are already-localised strings or plain data (numbers formatted by the caller).
 */
export function formatMessage(
  key: MessageKey,
  vars: Record<string, string>,
): LocalizedText {
  const substitute = (text: string): string =>
    text.replace(/\{(\w+)\}/g, (whole, name: string) =>
      Object.prototype.hasOwnProperty.call(vars, name) ? vars[name]! : whole,
    );
  const base = catalogue[key];
  return { ar: substitute(base.ar), en: substitute(base.en) };
}

export function ineligibilityMessage(
  code: IneligibilityReasonCode,
): LocalizedText {
  return catalogue[`eligibility.${code}` as MessageKey];
}

export function lineStateMessage(state: LineState): LocalizedText {
  return catalogue[`state.${state}` as MessageKey];
}

/** The merchant's plain word for a group of line states (schemas/state-group.ts). */
export function stateGroupMessage(group: MerchantStateGroup): LocalizedText {
  return catalogue[`state_group.${group}` as MessageKey];
}

/** Who did a thing, as the timeline names them (`return_event.actor_type`). */
export function actorMessage(actor: ActorType): LocalizedText {
  return catalogue[`actor.${actor}` as MessageKey];
}

export function gradeMessage(grade: Grade): LocalizedText {
  return catalogue[`grade.${grade}` as MessageKey];
}

/**
 * Reason text for anything a line's `reasonCode` may hold — a shopper's choice or a
 * system-originated cause. The two vocabularies have separate catalogue entries on purpose:
 * a return opened by a failed-delivery sweep renders "Delivery could not be completed",
 * never one of the shopper's picker labels.
 */
export function returnReasonMessage(code: LineReasonCode): LocalizedText {
  return catalogue[`reason.${code}` as MessageKey];
}

/** Narrower alias for the picker, which may only ever show shopper-chosen reasons. */
export function shopperReasonMessage(code: ReturnReasonCode): LocalizedText {
  return returnReasonMessage(code);
}

export function resolutionMessage(resolution: ResolutionType): LocalizedText {
  return catalogue[`resolution.${resolution}` as MessageKey];
}

export { MESSAGE_KEYS, ar, en };
export type { MessageKey };
